import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, CircularProgress, alpha,
} from '@mui/material';
import { timeTrackingApi, TimesheetRow } from '../services/api';

interface Props {
  dateRange: { startDate: string; endDate: string };
  userId?: number;
}

function formatHours(seconds: number): string {
  if (seconds === 0) return '-';
  const h = seconds / 3600;
  return h.toFixed(1);
}

function getDaysInRange(startDate: string, endDate: string): string[] {
  const days: string[] = [];
  const current = new Date(startDate);
  const end = new Date(endDate);
  while (current < end) {
    days.push(current.toISOString().slice(0, 10));
    current.setDate(current.getDate() + 1);
  }
  return days;
}

function formatDayHeader(dateStr: string): { day: string; date: string } {
  const d = new Date(dateStr + 'T12:00:00');
  return {
    day: d.toLocaleDateString('en-US', { weekday: 'short' }),
    date: d.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }),
  };
}

export default function TimesheetView({ dateRange, userId }: Props) {
  const [data, setData] = useState<TimesheetRow[]>([]);
  const [loading, setLoading] = useState(false);

  const days = useMemo(() => getDaysInRange(dateRange.startDate, dateRange.endDate), [dateRange]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await timeTrackingApi.getTimesheet({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        userId,
      });
      if (res.success) setData(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [dateRange, userId]);

  useEffect(() => { loadData(); }, [loadData]);

  // Build grid: rows = project+task, columns = days
  const grid = useMemo(() => {
    const rowMap = new Map<string, {
      project_name: string;
      task_name: string;
      project_id: number;
      task_id: number;
      dailySeconds: Map<string, number>;
      totalSeconds: number;
    }>();

    for (const row of data) {
      const key = `${row.project_id}|${row.task_id}`;
      if (!rowMap.has(key)) {
        rowMap.set(key, {
          project_name: row.project_name,
          task_name: row.task_name,
          project_id: row.project_id,
          task_id: row.task_id,
          dailySeconds: new Map(),
          totalSeconds: 0,
        });
      }
      const entry = rowMap.get(key)!;
      const existing = entry.dailySeconds.get(row.date) ?? 0;
      entry.dailySeconds.set(row.date, existing + row.total_seconds);
      entry.totalSeconds += row.total_seconds;
    }

    return Array.from(rowMap.values()).sort((a, b) => {
      const cmp = a.project_name.localeCompare(b.project_name);
      if (cmp !== 0) return cmp;
      return a.task_name.localeCompare(b.task_name);
    });
  }, [data]);

  // Column totals
  const columnTotals = useMemo(() => {
    const totals = new Map<string, number>();
    days.forEach(day => totals.set(day, 0));
    grid.forEach(row => {
      row.dailySeconds.forEach((secs, day) => {
        totals.set(day, (totals.get(day) ?? 0) + secs);
      });
    });
    return totals;
  }, [grid, days]);

  const grandTotal = useMemo(() => grid.reduce((s, r) => s + r.totalSeconds, 0), [grid]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '6px', overflow: 'hidden' }}>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, fontSize: '12.5px', minWidth: 160, position: 'sticky', left: 0, zIndex: 3, bgcolor: 'background.paper' }}>
                Project / Task
              </TableCell>
              {days.map(day => {
                const { day: dayName, date } = formatDayHeader(day);
                const isToday = day === new Date().toISOString().slice(0, 10);
                const isWeekend = [0, 6].includes(new Date(day + 'T12:00:00').getDay());
                return (
                  <TableCell
                    key={day}
                    align="center"
                    sx={{
                      fontWeight: 700,
                      fontSize: '11px',
                      minWidth: 65,
                      bgcolor: isToday ? (t) => alpha(t.palette.primary.main, 0.08) : isWeekend ? (t) => alpha(t.palette.action.hover, 0.3) : undefined,
                    }}
                  >
                    <Box>{dayName}</Box>
                    <Box sx={{ fontSize: '10.5px', fontWeight: 400, color: 'text.secondary' }}>{date}</Box>
                  </TableCell>
                );
              })}
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: '12.5px', minWidth: 65 }}>
                Total
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {grid.length === 0 ? (
              <TableRow>
                <TableCell colSpan={days.length + 2} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">No time entries for this period</Typography>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {grid.map((row, _idx) => (
                  <TableRow key={`${row.project_id}-${row.task_id}`} hover>
                    <TableCell sx={{ fontSize: '0.78rem', position: 'sticky', left: 0, zIndex: 1, bgcolor: 'background.paper' }}>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.78rem' }}>
                        {row.project_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '11px' }}>
                        {row.task_name}
                      </Typography>
                    </TableCell>
                    {days.map(day => {
                      const secs = row.dailySeconds.get(day) ?? 0;
                      const isToday = day === new Date().toISOString().slice(0, 10);
                      return (
                        <TableCell
                          key={day}
                          align="center"
                          sx={{
                            fontSize: '0.78rem',
                            fontWeight: secs > 0 ? 600 : 400,
                            color: secs > 0 ? 'text.primary' : 'text.secondary',
                            bgcolor: isToday ? (t) => alpha(t.palette.primary.main, 0.04) : undefined,
                          }}
                        >
                          {secs > 0 ? formatHours(secs) : '-'}
                        </TableCell>
                      );
                    })}
                    <TableCell align="center" sx={{ fontSize: '0.78rem', fontWeight: 700 }}>
                      {formatHours(row.totalSeconds)}
                    </TableCell>
                  </TableRow>
                ))}

                {/* Totals row */}
                <TableRow sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.04) }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', position: 'sticky', left: 0, zIndex: 1, bgcolor: (t) => alpha(t.palette.primary.main, 0.04) }}>
                    Daily Total
                  </TableCell>
                  {days.map(day => {
                    const total = columnTotals.get(day) ?? 0;
                    return (
                      <TableCell key={day} align="center" sx={{ fontWeight: 700, fontSize: '0.78rem' }}>
                        {total > 0 ? formatHours(total) : '-'}
                      </TableCell>
                    );
                  })}
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '13.5px', color: 'primary.main' }}>
                    {formatHours(grandTotal)}
                  </TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
